import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import AdminManagement from './index'
import { GenericForm } from './GenericForm'
import { useAdminAPI } from '../../hooks/Admin/useAdminAPI'
import { UserCog } from 'lucide-react'
import { buildSuggestedUsername } from './usernameHelpers'
import { useSelector } from 'react-redux'

const TechnicianForm = (props) => {
  const { initialData, onFieldChange: parentOnFieldChange } = props
  const [institutions, setInstitutions] = useState([])
  const [nodes, setNodes] = useState([])
  const { fetchEntities, checkUsernameAvailability } = useAdminAPI()
  const [usernameEditedManually, setUsernameEditedManually] = useState(Boolean(initialData?.username))
  const [externalUpdates, setExternalUpdates] = useState({})
  const [asyncFeedback, setAsyncFeedback] = useState({})
  const userRole = useSelector(state => state.user.role)
  const isAdminInstitucion = userRole === 'admin_institucion'
  const debounceRef = useRef(null)

  // Flatten initial data for the form if it comes from the nested API response
  useEffect(() => {
    setUsernameEditedManually(Boolean(initialData?.username))
  }, [initialData])

  const flattenedData = useMemo(() => {
    if (!initialData) return null
    return {
      ...initialData,
      idNodoCaptura: initialData.tecnico?.idNodoCaptura || initialData.idNodoCaptura,
      idInstitucion: initialData.tecnico?.nodoCaptura?.idInstitucion || initialData.idInstitucion
    }
  }, [initialData])

  useEffect(() => {
    let isMounted = true
    if (isAdminInstitucion) return

    const loadInstitutions = async () => {
      try {
        const result = await fetchEntities('instituciones')
        if (isMounted) {
          const items = Array.isArray(result) ? result : result.data || []
          setInstitutions(items)
        }
      } catch (error) {
        console.error('Failed to load institutions', error)
      }
    }
    loadInstitutions()
    return () => { isMounted = false }
  }, [fetchEntities, isAdminInstitucion])

  const loadNodes = useCallback(async (idInstitucion) => {
    if (!idInstitucion && !isAdminInstitucion) {
      setNodes([])
      return
    }
    const param = isAdminInstitucion ? 'propia' : idInstitucion
    try {
      const result = await fetchEntities(`nodos-captura/institucion/${param}`)
      const items = Array.isArray(result) ? result : result.data || []
      setNodes(items)
    } catch (error) {
      console.error('Failed to load nodes', error)
      setNodes([])
    }
  }, [fetchEntities, isAdminInstitucion])

  // Load initial nodes when editing
  useEffect(() => {
    if (isAdminInstitucion) {
      loadNodes()
    } else {
      const instId = flattenedData?.idInstitucion
      if (instId) {
        loadNodes(instId)
      }
    }
  }, [flattenedData?.idInstitucion, loadNodes, isAdminInstitucion])

  const handleFieldChange = useCallback((name, value, updatedData) => {
    if (name === 'idInstitucion') {
      loadNodes(value)
    }

    if (name === 'username') {
      setUsernameEditedManually((value || '').trim() !== '')
      setAsyncFeedback(prev => ({ ...prev, username: null }))
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }

    if (!initialData && !usernameEditedManually && ['nombre', 'apellido', 'dni'].includes(name)) {
      const { nombre, apellido, dni } = updatedData
      const baseSuggested = buildSuggestedUsername(nombre, apellido)

      if (!baseSuggested) {
        setExternalUpdates(prev => ({ ...prev, username: '' }))
        setAsyncFeedback(prev => ({ ...prev, username: null }))
      } else {
        setAsyncFeedback(prev => ({
          ...prev,
          username: { type: 'info', message: 'Verificando disponibilidad...' }
        }))

        if (debounceRef.current) {
          clearTimeout(debounceRef.current)
        }

        debounceRef.current = setTimeout(() => {
          checkUsernameAvailability(baseSuggested)
            .then(res => {
              if (res.disponible) {
                setExternalUpdates(prev => ({ ...prev, username: baseSuggested }))
                setAsyncFeedback(prev => ({ ...prev, username: { type: 'info', message: 'Usuario autogenerado sugerido' } }))
              } else {
                const dniStr = String(dni || '')
                if (dniStr.length >= 3) {
                  const fallback = `${baseSuggested}${dniStr.slice(-3)}`
                  checkUsernameAvailability(fallback).then(fbRes => {
                    if (fbRes.disponible) {
                      setExternalUpdates(prev => ({ ...prev, username: fallback }))
                      setAsyncFeedback(prev => ({ ...prev, username: { type: 'info', message: 'Usuario autogenerado sugerido (con DNI)' } }))
                    } else {
                      setExternalUpdates(prev => ({ ...prev, username: baseSuggested }))
                      setAsyncFeedback(prev => ({ ...prev, username: { type: 'warning', message: 'Sugerencias ocupadas. Ingresá un usuario manualmente.' } }))
                    }
                  }).catch(() => {
                    setAsyncFeedback(prev => ({ ...prev, username: { type: 'error', message: 'Error verificando usuario' } }))
                  })
                } else {
                  setExternalUpdates(prev => ({ ...prev, username: baseSuggested }))
                  setAsyncFeedback(prev => ({ ...prev, username: { type: 'warning', message: 'Usuario sugerido ocupado. Ingresá uno manualmente.' } }))
                }
              }
            })
            .catch(() => {
              setAsyncFeedback(prev => ({ ...prev, username: { type: 'error', message: 'Error verificando usuario' } }))
            })
        }, 500)
      }
    }

    if (parentOnFieldChange) {
      parentOnFieldChange(name, value, updatedData)
    }
  }, [loadNodes, parentOnFieldChange, initialData, usernameEditedManually, checkUsernameAvailability])

  const formFields = useMemo(() => [
    { key: 'dni', label: 'DNI', type: 'number', required: true },
    {
      key: 'genero',
      label: 'Género',
      required: true,
      type: 'select',
      options: [
        { value: 'Masculino', label: 'Masculino' },
        { value: 'Femenino', label: 'Femenino' }
      ]
    },
    { key: 'nombre', label: 'Nombre', required: true },
    { key: 'apellido', label: 'Apellido', required: true },
    { key: 'username', label: 'Usuario', required: true },
    { key: 'email', label: 'Correo electrónico', type: 'email', required: true },
    { key: 'clave', label: 'Contraseña', type: 'password', required: !initialData },
    {
      key: 'confirmarClave',
      label: 'Repetir contraseña',
      type: 'password',
      required: !initialData,
      matchesField: 'clave',
      errorMessage: 'Las contraseñas no coinciden.',
      clientOnly: true
    },
    ...(isAdminInstitucion ? [] : [{
      key: 'idInstitucion',
      label: 'Institución',
      type: 'select',
      required: true,
      options: institutions
        .filter(i => i.activo !== false || (initialData && initialData.idInstitucion === i.idInstitucion))
        .map(i => ({
          value: i.idInstitucion,
          label: `${i.nombre} - ${i.calle} ${i.numero}${i.activo === false ? ' (Inactivo)' : ''}`
        }))
    }]),
    {
      key: 'idNodoCaptura',
      label: 'Nodo Captura',
      type: 'select',
      required: true,
      options: nodes
        .filter(n => n.activo !== false || (initialData && (initialData.idNodoCaptura || initialData.tecnico?.idNodoCaptura) === n.idNodoCaptura))
        .map(n => ({
          value: n.idNodoCaptura,
          label: `${n.nombre}${n.activo === false ? ' (Inactivo)' : ''}`
        }))
    }
  ].filter(field => !initialData || !['clave', 'confirmarClave'].includes(field.key)), [initialData, institutions, nodes, isAdminInstitucion])

  return (
    <GenericForm
      {...props}
      initialData={flattenedData}
      fields={formFields}
      title='Técnico'
      onFieldChange={handleFieldChange}
      externalUpdates={externalUpdates}
      asyncFeedback={asyncFeedback}
    />
  )
}

const TechniciansManagement = () => {
  const [institutions, setInstitutions] = useState([])
  const { fetchEntities } = useAdminAPI()
  const userRole = useSelector(state => state.user.role)
  const isAdminInstitucion = userRole === 'admin_institucion'

  useEffect(() => {
    let isMounted = true
    if (isAdminInstitucion) return

    const loadInstitutions = async () => {
      try {
        const result = await fetchEntities('instituciones')
        if (isMounted) {
          const items = Array.isArray(result) ? result : result.data || []
          setInstitutions(items)
        }
      } catch (error) {
        console.error('Failed to load institutions', error)
      }
    }
    loadInstitutions()
    return () => { isMounted = false }
  }, [fetchEntities, isAdminInstitucion])

  const columns = useMemo(() => {
    const baseColumns = [
      {
        key: 'nombre',
        label: 'Nombre',
        render: (item) => `${item.nombre} ${item.apellido}`
      },
      { key: 'email', label: 'Email' },
      { key: 'dni', label: 'DNI' },
      { key: 'username', label: 'Usuario' }
    ]

    baseColumns.push({
      key: 'nodoCaptura',
      label: 'Nodo Captura',
      render: (item) => {
        const nodeName = item.tecnico?.nodoCaptura?.nombre || ' - '
        const instId = item.tecnico?.nodoCaptura?.idInstitucion
        const instName = isAdminInstitucion ? '' : (institutions.find(i => i.idInstitucion === instId)?.nombre || '')

        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span>{nodeName}</span>
            {!isAdminInstitucion && <span style={{ fontSize: '0.85em', color: 'gray' }}>{instName}</span>}
          </div>
        )
      }
    })

    return baseColumns
  }, [institutions, isAdminInstitucion])

  return (
    <AdminManagement
      entityType='Técnicos'
      headerSubtitle='Desde acá podés administrar usuarios técnicos y asignarlos a los nodos de captura para los que capturarán imágenes.'
      HeaderIcon={UserCog}
      columns={columns}
      apiEndpoint='usuarios/tecnicos'
      FormComponent={TechnicianForm}
      gridTemplateColumns='1.2fr 1.5fr 0.8fr 1fr 1.5fr 0.5fr'
      singularEntityName='Técnico'
    />
  )
}

export default TechniciansManagement
